import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Button,
  Alert,
  Fade,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  MusicNote as MusicNoteIcon,
  Movie as MovieIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { useDownload } from '../contexts/DownloadContext';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

const HistoryPage = () => {
  const { downloadHistory, clearHistory, downloadMedia } = useDownload();

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all download history?')) {
      clearHistory();
      toast.success('History cleared');
    }
  };

  const handleRedownload = (item) => {
    downloadMedia(item.mediaId, {
      format: item.format,
      quality: item.quality,
      platform: item.platform
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <AccessTimeIcon color="info" />;
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'youtube':
        return <YouTubeIcon sx={{ color: '#FF0000' }} />;
      case 'facebook':
        return <FacebookIcon sx={{ color: '#1877F2' }} />;
      case 'instagram':
        return <InstagramIcon sx={{ color: '#E4405F' }} />;
      case 'twitter':
        return <TwitterIcon sx={{ color: '#1DA1F2' }} />;
      case 'soundcloud':
      case 'spotify':
        return <MusicNoteIcon sx={{ color: '#1DB954' }} />;
      default:
        return <MovieIcon sx={{ color: '#8E8E93' }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Fade in={true} timeout={800}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Download History
            </Typography>
            {downloadHistory.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleClearHistory}
              >
                Clear History
              </Button>
            )}
          </Box>

          {downloadHistory.length > 0 ? (
            <List>
              {downloadHistory.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem 
                    sx={{ 
                      py: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'background.default' }}>
                        {getPlatformIcon(item.platform)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {item.title || item.mediaId}
                          </Typography>
                          <Chip
                            label={item.format.toUpperCase()}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                          <Chip
                            label={item.quality}
                            size="small"
                            variant="outlined"
                          />
                          {item.filesize && (
                            <Chip
                              label={formatFileSize(item.filesize)}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getStatusIcon(item.status)}
                            <Typography variant="caption" color="text.secondary">
                              {item.status === 'completed' ? 'Downloaded' : item.status}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.platform}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Download Again">
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRedownload(item)}
                          disabled={item.status === 'failed'}
                        >
                          <RepeatIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < downloadHistory.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Download History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your downloaded files will appear here
              </Typography>
            </Box>
          )}
        </Paper>
      </Fade>
    </Container>
  );
};

export default HistoryPage;